(() => {
  const parseVariants = (root) => {
    const source = root.querySelector('[data-product-variants]');
    if (!source) return [];
    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      return [];
    }
  };

  const showMedia = (root, mediaId) => {
    if (!mediaId) return;
    const target = root.querySelector(`[data-product-media="${mediaId}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const getSelectedOptions = (root) => {
    return Array.from(root.querySelectorAll('[data-product-option]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((group) => {
        const select = group.querySelector('[data-option-select]');
        if (select) return select.value;
        return group.querySelector('[data-option-value].is-active')?.dataset.optionValue || '';
      });
  };

  const initializeProduct = (root) => {
    const variants = parseVariants(root);
    const variantSelect = root.querySelector('[data-product-variant-select]');
    const price = root.querySelector('[data-product-price]');
    const compare = root.querySelector('[data-product-compare]');
    const submit = root.querySelector('[type="submit"][name="add"]');
    const form = root.querySelector('form[action*="/cart/add"]');

    const updateVariant = () => {
      if (!variants.length) return;
      const values = getSelectedOptions(root);
      const variant = variants.find((item) => item.options.every((value, index) => value === values[index]));

      if (!variant) {
        if (submit) {
          submit.disabled = true;
          submit.textContent = submit.dataset.soldLabel;
        }
        return;
      }

      if (variantSelect) variantSelect.value = String(variant.id);
      if (price) price.textContent = variant.price;
      if (compare) {
        compare.textContent = variant.compare_at_price || '';
        compare.classList.toggle('is-hidden', !variant.show_compare);
      }
      if (submit) {
        submit.disabled = !variant.available;
        submit.textContent = variant.available ? submit.dataset.addLabel : submit.dataset.soldLabel;
      }
      if (variant.featured_media_id) showMedia(root, variant.featured_media_id);

      if (variant.url && window.history?.replaceState) {
        window.history.replaceState({}, '', variant.url);
      }

      root.dispatchEvent(new CustomEvent('eryne:variant-change', { detail: { variant } }));
    };

    root.querySelectorAll('[data-product-thumbnail]').forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(root, thumb.dataset.productThumbnail));
    });

    root.querySelectorAll('[data-option-value]').forEach((button) => {
      button.addEventListener('click', () => {
        const group = button.closest('[data-product-option]');
        group?.querySelectorAll('[data-option-value]').forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        updateVariant();
      });
    });

    root.querySelectorAll('[data-option-select]').forEach((select) => {
      select.addEventListener('change', updateVariant);
    });

    root.querySelectorAll('[data-material-split]').forEach((group) => {
      const current = group.querySelector('.eryne-material-current');
      const choices = Array.from(group.querySelectorAll('[data-material-choice]')).map((node) => node.dataset.materialChoice);
      const colorButtons = Array.from(group.querySelectorAll('[data-material-color]'));
      const purityButtons = Array.from(group.querySelectorAll('[data-material-purity]'));

      const normalize = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const colorOf = (value) => {
        const v = normalize(value);
        if (v.includes('rose')) return 'rose';
        if (v.includes('yellow')) return 'yellow';
        if (v.includes('white') || v.includes('silver') || v.includes('platinum')) return 'white';
        return '';
      };
      const purityOf = (value) => {
        const v = normalize(value).replace(/\s/g, '');
        if (v.includes('925') || v.includes('sterling')) return '925';
        if (v.includes('10k')) return '10k';
        if (v.includes('14k')) return '14k';
        if (v.includes('18k')) return '18k';
        if (v.includes('platinum')) return 'platinum';
        return '';
      };
      const renderSplit = (value) => {
        const color = colorOf(value);
        const purity = purityOf(value);
        colorButtons.forEach((button) => {
          const active = button.dataset.materialColor === color;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        purityButtons.forEach((button) => button.classList.toggle('is-active', button.dataset.materialPurity === purity));
      };
      const choose = ({ color, purity }) => {
        let match = choices.find((value) => (!color || colorOf(value) === color) && (!purity || purityOf(value) === purity));
        if (!match && color) match = choices.find((value) => colorOf(value) === color);
        if (!match && purity) match = choices.find((value) => purityOf(value) === purity);
        if (!match) return;
        current.dataset.optionValue = match;
        renderSplit(match);
        updateVariant();
      };

      colorButtons.forEach((button) => button.addEventListener('click', () => {
        choose({ color: button.dataset.materialColor, purity: purityOf(current.dataset.optionValue) });
      }));
      purityButtons.forEach((button) => button.addEventListener('click', () => {
        choose({ color: colorOf(current.dataset.optionValue), purity: button.dataset.materialPurity });
      }));
      renderSplit(current?.dataset.optionValue || '');
    });

    const quantity = root.querySelector('input[name="quantity"]');
    root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Math.max(Number(quantity.min || 1), Number(quantity.value || 1) - 1));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });
    root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Number(quantity.value || 1) + 1);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    });

    form?.addEventListener('submit', () => {
      if (submit && !submit.disabled) submit.textContent = 'Adding…';
    });
  };

  const initializeWishlists = () => {
    document.querySelectorAll('[data-eryne-wishlist]').forEach((button) => {
      const handle = button.dataset.eryneWishlist;
      let saved = false;
      try {
        saved = window.localStorage.getItem(`eryne-wishlist-${handle}`) === 'true';
      } catch (error) {
        saved = false;
      }

      const render = () => {
        button.classList.toggle('is-saved', saved);
        button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      };
      render();

      button.addEventListener('click', () => {
        saved = !saved;
        try {
          window.localStorage.setItem(`eryne-wishlist-${handle}`, String(saved));
        } catch (error) {
          /* Wishlist still works visually when storage is blocked. */
        }
        render();
      });
    });
  };

  const initialize = () => {
    document.querySelectorAll('[data-eryne-product]').forEach(initializeProduct);
    initializeWishlists();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
